import { useEffect, useState } from "react";
import { Type, Image as ImageIcon, Video, AudioLines } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import Reveal from "@/components/Reveal";
import claudeCode from "@/assets/runtime-marks/claude-code.svg";
import codex from "@/assets/runtime-marks/codex.svg";
import cursor from "@/assets/runtime-marks/cursor.svg";
import antigravity from "@/assets/runtime-marks/antigravity.png";
import lovable from "@/assets/runtime-marks/lovable.svg";
import manus from "@/assets/runtime-marks/manus.png";
import openclaw from "@/assets/runtime-marks/openclaw.svg";
import hermes from "@/assets/runtime-marks/hermes.png";
import replit from "@/assets/runtime-marks/replit.svg";
import base44 from "@/assets/runtime-marks/base44.png";
import perplexity from "@/assets/runtime-marks/perplexity.svg";


/**
 * IntegrationsRotator — two pitch cards matching the reference 1:1.
 * Left: vertically marqueeing 5-col integration grid with top/bottom fade mask.
 * Right: pyramidal cluster of model logos in white circles.
 */

const iconify = (slug: string) => `https://api.iconify.design/logos/${slug}.svg`;
const simple = (slug: string, color?: string) =>
  `https://api.iconify.design/simple-icons/${slug}.svg${color ? `?color=%23${color}` : ""}`;

type Tile = { name: string; src: string };

// Left card — many tiles, scrolled vertically as a marquee
const INTEGRATION_TILES: Tile[] = [
  { name: "Notion", src: iconify("notion-icon") },
  { name: "YouTube", src: iconify("youtube-icon") },
  { name: "Gmail", src: iconify("google-gmail") },
  { name: "Calendar", src: iconify("google-calendar") },
  { name: "Drive", src: iconify("google-drive") },
  { name: "LinkedIn", src: iconify("linkedin-icon") },
  { name: "X", src: iconify("x") },
  { name: "QuickBooks", src: simple("quickbooks", "2CA01C") },
  { name: "Crunchbase", src: simple("crunchbase", "0288D1") },
  { name: "Zapier", src: simple("zapier", "FF4A00") },
  { name: "Salesforce", src: iconify("salesforce") },
  { name: "Confluence", src: iconify("confluence") },
  { name: "Jira", src: iconify("jira") },
  { name: "Docs", src: simple("googledocs", "4285F4") },
  { name: "Sheets", src: simple("googlesheets", "34A853") },
  { name: "Slack", src: iconify("slack-icon") },
  { name: "Asana", src: simple("asana", "F06A6A") },
  { name: "Airtable", src: iconify("airtable") },
  { name: "Dropbox", src: iconify("dropbox") },
  { name: "Figma", src: iconify("figma") },
  { name: "GitHub", src: iconify("github-icon") },
  { name: "Stripe", src: simple("stripe", "635BFF") },
  { name: "HubSpot", src: simple("hubspot", "FF7A59") },
  { name: "Linear", src: simple("linear", "5E6AD2") },
  { name: "Intercom", src: iconify("intercom-icon") },
  { name: "Trello", src: iconify("trello") },
  { name: "Monday", src: iconify("monday") },
  { name: "Discord", src: iconify("discord") },
  { name: "Shopify", src: iconify("shopify") },
  { name: "WordPress", src: iconify("wordpress-icon") },
  { name: "AWS", src: simple("amazonaws", "FF9900") },
  { name: "Azure", src: iconify("azure") },
  { name: "Zoom", src: iconify("zoom") },
  { name: "Teams", src: iconify("microsoft-teams") },
  { name: "Telegram", src: iconify("telegram") },
];

type ModelTile = { name: string; src: string; mono?: boolean; tint?: string };

// Right card — agentic runtimes only (match HeroRuntimeStrip), no duplicates
const MODEL_TILES: ModelTile[] = [
  { name: "Claude Code", src: claudeCode, mono: true },
  { name: "Codex", src: codex, mono: true },
  { name: "Cursor", src: cursor, mono: true },
  { name: "Antigravity", src: antigravity, mono: true },
  { name: "Lovable", src: lovable, mono: true },
  { name: "Manus", src: manus, mono: true },
  { name: "Replit", src: replit, mono: true },
  { name: "Base44", src: base44, mono: true },
  { name: "Perplexity", src: perplexity, mono: true },
  { name: "Hermes", src: hermes, mono: true },
  { name: "OpenClaw", src: openclaw, mono: true },
];

const Pill = ({ icon: Icon, label }: { icon: React.ElementType; label: string }) => (
  <span
    className="inline-flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-[10px] font-normal text-foreground whitespace-nowrap"
    style={{ boxShadow: "0 0 0 1px hsl(var(--foreground) / 0.10)" }}
  >
    <Icon className="h-3 w-3 text-muted-foreground shrink-0" strokeWidth={2} />
    {label}
  </span>
);

const CardShell = ({ children, tone = "warm" }: { children: React.ReactNode; tone?: "warm" | "cool" }) => {
  const wash =
    tone === "warm"
      ? "radial-gradient(140% 90% at 15% 0%, hsl(var(--accent) / 0.30) 0%, hsl(var(--accent) / 0.10) 32%, transparent 68%), radial-gradient(90% 70% at 100% 0%, hsl(38 95% 70% / 0.09) 0%, transparent 60%)"
      : "radial-gradient(120% 85% at 85% 0%, hsl(220 60% 60% / 0.10) 0%, hsl(260 50% 55% / 0.05) 38%, transparent 72%), linear-gradient(180deg, hsl(var(--foreground) / 0.02) 0%, transparent 60%)";

  const sheen =
    tone === "warm"
      ? "linear-gradient(180deg, hsl(0 0% 100% / 0.3) 0%, transparent 40%)"
      : "linear-gradient(180deg, hsl(0 0% 100% / 0.2) 0%, transparent 45%)";

  return (
    <div
      className="relative rounded-[16px] bg-card overflow-hidden h-full"
      style={{
        boxShadow:
          "0 0 0 1px hsl(var(--foreground) / 0.06), 0 4px 12px -10px hsl(var(--foreground) / 0.12)",
      }}
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[55%] pointer-events-none z-0"
        style={{ background: wash }}
      />
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[20%] pointer-events-none z-0 mix-blend-overlay"
        style={{ background: sheen }}
      />
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
};

/** Angled marquee — 3 horizontally scrolling rows rotated ~-20deg */
const AngledGrid = ({
  tiles,
}: {
  tiles: { name: string; src: string; mono?: boolean }[];
  perRow?: number;
}) => {
  const third = Math.ceil(tiles.length / 3);
  const rowsBase = [
    tiles.slice(0, third),
    tiles.slice(third, third * 2),
    tiles.slice(third * 2),
  ].filter((r) => r.length > 0);

  const directions = [-1, 1, -1];
  const durations = [22, 26, 24];

  return (
    <div
      className="relative w-full"
      style={{
        transform: "rotate(-20deg)",
        minHeight: 200,
        paddingTop: 28,
        overflowX: "clip",
        overflowY: "visible",
        maskImage:
          "linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%)",
      }}
    >
      <div className="flex flex-col items-stretch" style={{ gap: 14 }}>
        {rowsBase.slice(0, 3).map((row, ri) => {
          const dir = directions[ri] ?? -1;
          const duration = durations[ri] ?? 24;
          const loopRow = [...row, ...row];
          return (
            <div key={ri} className="w-full" style={{ overflowX: "clip", overflowY: "visible" }}>
              <div
                className="flex items-center"
                style={{
                  gap: 14,
                  width: "max-content",
                  animation: `${dir < 0 ? "integrationsMarqueeLeft" : "integrationsMarqueeRight"} ${duration}s linear infinite`,
                }}
              >
                {loopRow.map((t, i) => {
                  const isMono = t.mono;
                  return (
                    <div
                      key={`${t.name}-${ri}-${i}`}
                      className="group relative flex items-center justify-center shrink-0"
                      style={{ width: 40, height: 40 }}
                      title={t.name}
                    >
                      <div
                        className="flex items-center justify-center rounded-full bg-card/80"
                        style={{
                          width: 40,
                          height: 40,
                          boxShadow: "0 0 0 1px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.06)",
                        }}
                      >
                        <img
                          src={t.src}
                          alt={t.name}
                          loading="lazy"
                          decoding="async"
                          className={
                            isMono
                              ? "object-contain [filter:brightness(0)_saturate(100%)] dark:[filter:brightness(0)_saturate(100%)_invert(1)]"
                              : "object-contain"
                          }
                          style={{ height: 22, width: 22 }}
                        />
                      </div>
                      <span
                        role="tooltip"
                        className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap rounded-md bg-foreground text-background text-[10px] font-medium px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-md"
                      >
                        {t.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Vertical marquee — scrolls unique tile rows, no duplication. */
const VerticalMarquee = ({
  tiles,
  cols = 4,
  mobileCols,
  mono = false,
}: {
  tiles: { name: string; src: string; mono?: boolean }[];
  cols?: number;
  mobileCols?: number;
  mono?: boolean;
}) => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  const activeCols = isMobile && mobileCols ? mobileCols : cols;
  const rows: typeof tiles[] = [];
  for (let i = 0; i < tiles.length; i += activeCols) rows.push(tiles.slice(i, i + activeCols));

  const rowH = isMobile ? 38 : 32;
  const gap = isMobile ? 14 : 12;
  const containerH = isMobile ? 190 : 160;
  const totalH = rows.length * rowH + (rows.length - 1) * gap;
  const scroll = Math.max(0, totalH - containerH);

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        height: containerH,
        maskImage:
          "linear-gradient(180deg, transparent 0%, #000 18%, #000 80%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(180deg, transparent 0%, #000 18%, #000 80%, transparent 100%)",
      }}
    >
      <div
        className="flex flex-col"
        style={{
          gap,
          animation: scroll > 0 ? `integrationsVertical ${rows.length * 2.8}s linear infinite alternate` : undefined,
          ["--integrations-scroll" as string]: `${-scroll}px`,
        }}
      >
        {rows.map((row, ri) => (
          <div
            key={ri}
            className="grid gap-x-3"
            style={{ gridTemplateColumns: `repeat(${activeCols}, minmax(0, 1fr))` }}
          >
            {row.map((t, i) => {
              const isMono = mono || (t as any).mono;
              return (
                <div
                  key={`${t.name}-${ri}-${i}`}
                  className="group relative flex items-center justify-center"
                  style={{ height: rowH }}
                  title={t.name}
                >
                  <div
                    className={
                      isMono
                        ? "flex items-center justify-center"
                        : "flex items-center justify-center rounded-md dark:bg-white/[0.92] dark:p-[2px]"
                    }
                  >
                    <img
                      src={t.src}
                      alt={t.name}
                      loading="lazy"
                      decoding="async"
                      className={
                        isMono
                          ? "object-contain [filter:brightness(0)_saturate(100%)] dark:[filter:brightness(0)_saturate(100%)_invert(1)]"
                          : "object-contain"
                      }
                      style={{ height: isMobile ? 28 : 24, width: isMobile ? 28 : 24 }}
                    />
                  </div>
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap rounded-md bg-foreground text-background text-[10px] font-medium px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-md"
                  >
                    {t.name}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};


const IntegrationsRotator = () => {
  return (
    <section id="integrations-rotator" className="pt-6 pb-4 sm:pt-8 sm:pb-5">
      <style>{`
        @keyframes integrationsMarqueeLeft {
          from { transform: translate3d(0,0,0); }
          to { transform: translate3d(-50%,0,0); }
        }
        @keyframes integrationsMarqueeRight {
          from { transform: translate3d(-50%,0,0); }
          to { transform: translate3d(0,0,0); }
        }
        @keyframes integrationsVertical {
          from { transform: translate3d(0,0,0); }
          to { transform: translate3d(0,var(--integrations-scroll),0); }
        }
      `}</style>
      <div className="max-w-content mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeader
            align="center"
            eyebrow="The orchestration layer"
            title={<>Runs on anything. <em>Integrates with everything.</em></>}
            subtitle="Value compounds with every task, self improves, creates its own knowledge hub."
            className="!mb-6"
          />
        </Reveal>

        {(() => {
          const LeftCard = (
            <CardShell tone="warm">
              <div className="relative h-full px-4 py-4 sm:px-5 sm:py-5 flex gap-3">
                <div className="flex flex-col justify-center w-[46%] shrink-0">
                  <div className="text-[11px] sm:text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
                    Skills Marketplace
                  </div>
                  <h3 className="text-[17px] sm:text-[18px] font-normal leading-[1.15] tracking-[-0.015em] text-foreground">
                    Connect agents to the tools you already use
                  </h3>
                  <div className="pt-2">
                    <span className="text-[13px] text-muted-foreground">skills / mcp</span>
                  </div>
                </div>
                <div className="flex-1 flex items-center justify-end overflow-hidden">
                  <div className="w-full max-w-[280px] sm:max-w-[200px]">
                    <VerticalMarquee tiles={INTEGRATION_TILES} cols={4} mobileCols={4} />
                  </div>
                </div>
              </div>
            </CardShell>
          );

          const RightCard = (
            <CardShell tone="cool">
              <div className="relative h-full px-4 py-4 sm:px-5 sm:py-5 flex gap-3">
                <div className="flex flex-col justify-center w-[46%] shrink-0">
                  <div className="text-[11px] sm:text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
                    Unified intelligence
                  </div>
                  <h3 className="text-[17px] sm:text-[18px] font-normal leading-[1.15] tracking-[-0.015em] text-foreground">
                    Any model, any modality, under your control
                  </h3>
                  <div className="pt-2 flex flex-nowrap items-center gap-3">
                    <Type className="h-[18px] w-[18px] text-muted-foreground shrink-0" strokeWidth={2} aria-label="Text" />
                    <ImageIcon className="h-[18px] w-[18px] text-muted-foreground shrink-0" strokeWidth={2} aria-label="Image" />
                    <Video className="h-[18px] w-[18px] text-muted-foreground shrink-0" strokeWidth={2} aria-label="Video" />
                    <AudioLines className="h-[18px] w-[18px] text-muted-foreground shrink-0" strokeWidth={2} aria-label="Audio" />
                  </div>
                </div>
                <div className="flex-1 flex items-center justify-end overflow-hidden">
                  <div className="w-full">
                    <AngledGrid tiles={MODEL_TILES} perRow={7} />
                  </div>
                </div>
              </div>
            </CardShell>
          );

          return (
            <Reveal>
              {/* Desktop: side-by-side grid */}
              <div className="hidden md:grid md:grid-cols-2 gap-3 items-stretch">
                {LeftCard}
                {RightCard}
              </div>

              {/* Mobile: continuous horizontal marquee */}
              <div className="md:hidden mane-mobile-cards-marquee">
                <div className="mane-mobile-cards-marquee__track">
                  {[0, 1].map((dup) => (
                    <div key={dup} className="mane-mobile-cards-marquee__set">
                      <div className="mane-mobile-cards-marquee__item">{LeftCard}</div>
                      <div className="mane-mobile-cards-marquee__item">{RightCard}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          );
        })()}

      </div>
    </section>
  );
};

export default IntegrationsRotator;
